import { Node, mergeAttributes } from '@tiptap/core'
import { Plugin } from '@tiptap/pm/state'
import { storeToRefs } from 'pinia'

import { useEditorStore } from '@/stores'
import { DynamicNodeAttrs, TableLoopAttrs } from '@/types'

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    tableLoop: {
      insertTableLoop: (attrs: TableLoopAttrs) => ReturnType
      openTableLoopDialog: (attrs?: TableLoopAttrs, callback?: (attrs: DynamicNodeAttrs) => void) => ReturnType
    }
  }
}

const tableLoopName = 'tableLoop'

export const TableLoop = Node.create({
  name: tableLoopName,

  group: 'block',
  content: '',
  atom: true,
  draggable: true,
  isolating: false,

  addAttributes() {
    return {
      arrayName: {
        default: '',
        parseHTML: el => {
          return el.getAttribute('data-array-name') || ''
        },
        renderHTML: attrs => (attrs.arrayName ? { 'data-array-name': attrs.arrayName } : {}),
      },
      columnTitles: {
        default: [],
        parseHTML: el => {
          const titlesStr = el.getAttribute('data-column-titles')
          return titlesStr ? JSON.parse(titlesStr) : []
        },
        renderHTML: attrs => {
          return attrs.columnTitles && attrs.columnTitles.length > 0
            ? { 'data-column-titles': JSON.stringify(attrs.columnTitles) }
            : {}
        },
      },
      columnFields: {
        default: [],
        parseHTML: el => {
          const fieldsStr = el.getAttribute('data-column-fields')
          return fieldsStr ? JSON.parse(fieldsStr) : []
        },
        renderHTML: attrs => {
          return attrs.columnFields && attrs.columnFields.length > 0
            ? { 'data-column-fields': JSON.stringify(attrs.columnFields) }
            : {}
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-table-loop="true"]',
        priority: 100,
      },
      {
        tag: 'div[data-table-loop]',
        priority: 50,
      },
    ]
  },

  renderHTML({ node, HTMLAttributes }) {
    const arrayName = node.attrs.arrayName || '[]'
    const arrayVariable = 'item'
    const columnTitles = node.attrs.columnTitles || []
    const columnFields = node.attrs.columnFields || []

    // Build the table header cells as proper HTML elements
    const theadCells = columnTitles.map((title: string) => ['th', {}, title])

    // Build table data cells - one for each field with template variable
    const tbodyCells =
      columnFields.length > 0
        ? columnFields.map((field: string, index: number) => {
            const templateVar = field === 'loop.index' ? '{{ loop.index }}' : `{{ ${arrayVariable}.${field} }}`
            return ['td', { 'data-column-index': String(index), 'data-field': field }, templateVar]
          })
        : []

    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-table-loop': 'true',
        'data-array-name': arrayName,
        'data-column-titles': JSON.stringify(columnTitles),
        'data-column-fields': JSON.stringify(columnFields),
      }),
      [
        'table',
        {},
        // Table header
        ['thead', {}, ['tr', {}, ...theadCells]],
        // Table body with loop inside
        [
          'tbody',
          {},
          // Loop start marker as text content
          `{% for ${arrayVariable} in ${arrayName} %}`,
          // Content row with separate cells containing field variables
          ['tr', { class: 'table-loop-content' }, ...tbodyCells],
          // Loop end marker as text content
          '{% endfor %}',
        ],
      ],
    ]
  },

  addCommands() {
    const editor = this.editor
    return {
      insertTableLoop:
        attrs =>
        ({ chain }) => {
          return chain()
            .insertContent({
              type: this.name,
              attrs,
            })
            .run()
        },

      openTableLoopDialog: (attrs, callback) => () => {
        const editorStore = useEditorStore()
        const { dynamicDialogType, dynamicDialogAttrs, dynamicDialogCallback } = storeToRefs(editorStore)
        dynamicDialogType.value = 'tableLoop'
        dynamicDialogAttrs.value = attrs
        dynamicDialogCallback.value = callback ?? (attrs => editor?.commands.insertTableLoop(attrs as TableLoopAttrs))
        return true
      },
    }
  },

  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        props: {
          handleClickOn(view, _pos, node, nodePos, event) {
            if (node.type.name !== tableLoopName || !editor.isEditable) return false
            const target = event?.target as HTMLElement | null
            if (!target) return false

            // Allow clicking on the table loop container or table elements
            const block = target.closest('[data-table-loop="true"]')
            if (!block) return false

            // Don't intercept clicks on td elements (allow content editing)
            if (target.tagName === 'TD' || target.closest('td')) return false

            event.preventDefault()
            event.stopPropagation()

            editor?.commands.openTableLoopDialog(node.attrs as TableLoopAttrs, attrs => {
              view.dispatch(view.state.tr.setNodeMarkup(nodePos, undefined, attrs))
            })
            return true
          },
        },
      }),
    ]
  },
})
